// 本地用户档案管理服务 - 完全基于localStorage的CRUD操作
import { 
  UserRecord, 
  UserCard, 
  CreateUserRecordRequest, 
  AssessmentRecord,
  ComprehensiveAssessmentResult 
} from '../types/index';
import { authService } from './authService';

// 本地存储键名
const LOCAL_USERS_KEY = 'icope_local_users';
const LOCAL_USER_INDEX_KEY = 'icope_user_index';

class LocalUserService {
  
  // 获取所有本地用户ID列表
  private getUserIndex(): string[] {
    try {
      const index = localStorage.getItem(LOCAL_USER_INDEX_KEY);
      return index ? JSON.parse(index) : [];
    } catch (e) {
      console.warn('获取用户索引失败:', e);
      return [];
    }
  }

  // 更新用户ID索引
  private updateUserIndex(userIds: string[]): void {
    try {
      localStorage.setItem(LOCAL_USER_INDEX_KEY, JSON.stringify(userIds));
    } catch (e) {
      console.error('更新用户索引失败:', e);
    }
  }

  // 添加用户到索引
  private addToIndex(userId: string): void {
    const index = this.getUserIndex();
    if (!index.includes(userId)) {
      index.push(userId);
      this.updateUserIndex(index);
    }
  }

  // 从索引中移除用户
  private removeFromIndex(userId: string): void {
    const index = this.getUserIndex();
    const filteredIndex = index.filter(id => id !== userId);
    this.updateUserIndex(filteredIndex);
  }

  // 生成唯一用户ID
  private generateUserId(): string {
    return `local_user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 创建用户档案
  async createUserRecord(request: CreateUserRecordRequest, providedUserId?: string): Promise<string> {
    try {
      // 允许传入指定的userId，否则生成新的
      const userId = providedUserId || this.generateUserId();
      const now = new Date().toISOString();
      
      const userRecord: UserRecord = {
        id: userId,
        personalInfo: request.personalInfo,
        assessmentHistory: [],
        personalizedPlans: [],
        status: 'active',
        createdAt: now,
        lastUpdated: now,
        createdBy: 'local_system',
        tags: request.tags || []
      };

      // 保存用户数据
      localStorage.setItem(`${LOCAL_USERS_KEY}_${userId}`, JSON.stringify(userRecord));
      
      // 添加到索引
      this.addToIndex(userId);
      
      // TODO: 添加到当前照护者的ownedProfiles (暂时移除避免循环依赖)
      // authService.addUserProfile(userId);
      
      console.log('✅ 本地用户档案创建成功:', userId);
      return userId;
    } catch (error) {
      console.error('❌ 创建本地用户档案失败:', error);
      throw error;
    }
  }

  // 获取所有活跃用户的卡片信息
  async getAllActiveUsers(): Promise<UserCard[]> {
    try {
      const userIds = this.getUserIndex();
      const userCards: UserCard[] = [];

      for (const userId of userIds) {
        try {
          const userData = localStorage.getItem(`${LOCAL_USERS_KEY}_${userId}`);
          if (userData) {
            const userRecord: UserRecord = JSON.parse(userData);
            
            // 只包括活跃用户
            if (userRecord.status === 'active') {
              const userCard: UserCard = {
                id: userRecord.id,
                name: userRecord.personalInfo.name,
                age: userRecord.personalInfo.age,
                gender: userRecord.personalInfo.gender,
                avatar: userRecord.personalInfo.avatar,
                lastAssessmentDate: userRecord.assessmentHistory.length > 0 
                  ? userRecord.assessmentHistory[userRecord.assessmentHistory.length - 1].assessmentDate 
                  : undefined,
                assessmentCount: userRecord.assessmentHistory.length,
                status: userRecord.status,
                createdAt: userRecord.createdAt
              };
              userCards.push(userCard);
            }
          }
        } catch (e) {
          console.warn(`解析用户数据失败: ${userId}`, e);
          // 清理损坏的数据
          this.removeFromIndex(userId);
          localStorage.removeItem(`${LOCAL_USERS_KEY}_${userId}`);
        }
      }

      // 按最后更新时间排序
      userCards.sort((a, b) => {
        const aTime = a.lastAssessmentDate || a.createdAt;
        const bTime = b.lastAssessmentDate || b.createdAt;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      console.log(`📋 获取到 ${userCards.length} 个本地用户档案`);
      return userCards;
    } catch (error) {
      console.error('❌ 获取本地用户列表失败:', error);
      throw error;
    }
  }

  // 获取单个用户完整档案
  async getUserRecord(userId: string): Promise<UserRecord | null> {
    try {
      console.log('🔍 [LocalUserService] 查找用户档案:', userId);
      const storageKey = `${LOCAL_USERS_KEY}_${userId}`;
      console.log('🔍 [LocalUserService] 存储键名:', storageKey);
      
      const userData = localStorage.getItem(storageKey);
      console.log('🔍 [LocalUserService] 原始数据长度:', userData?.length || 0);
      
      if (userData) {
        const userRecord: UserRecord = JSON.parse(userData);
        console.log('✅ [LocalUserService] 用户档案找到:', userRecord.personalInfo?.name);
        return userRecord;
      } else {
        console.log('❌ [LocalUserService] 本地用户档案不存在:', userId);
        console.log('🔍 [LocalUserService] 当前localStorage键名:', Object.keys(localStorage).filter(key => key.includes('icope_local_users')));
        return null;
      }
    } catch (error) {
      console.error('❌ 获取本地用户档案失败:', error);
      throw error;
    }
  }

  // 获取单个用户完整档案的别名方法（为了兼容性）
  async getUserById(userId: string): Promise<UserRecord | null> {
    return this.getUserRecord(userId);
  }

  // 更新用户档案基本信息
  async updateUserRecord(userId: string, updates: Partial<UserRecord>): Promise<void> {
    try {
      const currentRecord = await this.getUserRecord(userId);
      if (!currentRecord) {
        throw new Error('用户档案不存在');
      }

      const updatedRecord: UserRecord = {
        ...currentRecord,
        ...updates,
        id: userId, // 确保ID不被更改
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem(`${LOCAL_USERS_KEY}_${userId}`, JSON.stringify(updatedRecord));
      console.log('✅ 本地用户档案更新成功:', userId);
    } catch (error) {
      console.error('❌ 更新本地用户档案失败:', error);
      throw error;
    }
  }

  // 为用户添加评估结果
  async addAssessmentResult(
    userId: string, 
    result: ComprehensiveAssessmentResult, 
    notes?: string,
    conductedBy?: string
  ): Promise<void> {
    try {
      const userRecord = await this.getUserRecord(userId);
      if (!userRecord) {
        throw new Error('用户档案不存在');
      }

      // 创建新的评估记录
      const assessmentRecord: AssessmentRecord = {
        id: `assessment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        assessmentDate: new Date().toISOString(),
        result: result,
        notes: notes,
        conductedBy: conductedBy,
        duration: undefined
      };

      // 更新评估历史
      const updatedHistory = [...userRecord.assessmentHistory, assessmentRecord];
      
      await this.updateUserRecord(userId, {
        assessmentHistory: updatedHistory
      });

      console.log('✅ 本地评估结果添加成功:', userId);
    } catch (error) {
      console.error('❌ 添加本地评估结果失败:', error);
      throw error;
    }
  }

  // 获取用户评估历史
  async getAssessmentHistory(userId: string): Promise<AssessmentRecord[]> {
    try {
      const userRecord = await this.getUserRecord(userId);
      return userRecord?.assessmentHistory || [];
    } catch (error) {
      console.error('❌ 获取本地评估历史失败:', error);
      throw error;
    }
  }

  // 删除用户档案（软删除，标记为inactive）
  async deactivateUserRecord(userId: string): Promise<void> {
    try {
      await this.updateUserRecord(userId, { 
        status: 'inactive' 
      });
      console.log('✅ 本地用户档案已停用:', userId);
    } catch (error) {
      console.error('❌ 停用本地用户档案失败:', error);
      throw error;
    }
  }

  // 彻底删除用户档案
  async deleteUserRecord(userId: string): Promise<void> {
    try {
      // 从索引中移除
      this.removeFromIndex(userId);
      
      // 删除用户数据
      localStorage.removeItem(`${LOCAL_USERS_KEY}_${userId}`);
      
      // 清理相关评估历史数据（如果有旧格式数据）
      localStorage.removeItem(`assessmentHistory_${userId}`);
      
      console.log('✅ 本地用户档案已删除:', userId);
    } catch (error) {
      console.error('❌ 删除本地用户档案失败:', error);
      throw error;
    }
  }

  // 搜索用户（按姓名）
  async searchUsers(searchTerm: string): Promise<UserCard[]> {
    try {
      const allUsers = await this.getAllActiveUsers();
      const filteredUsers = allUsers.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      console.log(`🔍 本地搜索 "${searchTerm}" 找到 ${filteredUsers.length} 个结果`);
      return filteredUsers;
    } catch (error) {
      console.error('❌ 本地搜索用户失败:', error);
      throw error;
    }
  }

  // 生成默认头像URL（基于姓名）
  generateDefaultAvatar(name: string, gender: '男' | '女'): string {
    const baseUrl = 'https://api.dicebear.com/7.x';
    const style = gender === '男' ? 'male' : 'female';
    return `${baseUrl}/${style}/svg?seed=${encodeURIComponent(name)}`;
  }

  // 数据清理和维护方法
  async cleanupCorruptedData(): Promise<void> {
    console.log('🧹 开始清理损坏的本地数据...');
    const userIds = this.getUserIndex();
    const validIds: string[] = [];

    for (const userId of userIds) {
      try {
        const userData = localStorage.getItem(`${LOCAL_USERS_KEY}_${userId}`);
        if (userData) {
          JSON.parse(userData); // 测试是否能正确解析
          validIds.push(userId);
        } else {
          console.warn(`清理: 用户数据不存在 ${userId}`);
        }
      } catch (e) {
        console.warn(`清理: 删除损坏的用户数据 ${userId}`, e);
        localStorage.removeItem(`${LOCAL_USERS_KEY}_${userId}`);
      }
    }

    // 更新索引
    if (validIds.length !== userIds.length) {
      this.updateUserIndex(validIds);
      console.log(`🧹 清理完成: ${userIds.length} → ${validIds.length} 用户`);
    }
  }

  // 获取存储统计信息
  async getStorageStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    totalAssessments: number;
    storageSize: string;
  }> {
    const allUsers = await this.getAllActiveUsers();
    const userIds = this.getUserIndex();
    let totalAssessments = 0;
    let inactiveCount = 0;

    // 计算总评估数和非活跃用户数
    for (const userId of userIds) {
      const userData = localStorage.getItem(`${LOCAL_USERS_KEY}_${userId}`);
      if (userData) {
        try {
          const userRecord: UserRecord = JSON.parse(userData);
          totalAssessments += userRecord.assessmentHistory.length;
          if (userRecord.status !== 'active') {
            inactiveCount++;
          }
        } catch (e) {
          // 忽略损坏的数据
        }
      }
    }

    // 估算存储大小
    let storageSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(LOCAL_USERS_KEY) || key === LOCAL_USER_INDEX_KEY)) {
        storageSize += (localStorage.getItem(key) || '').length;
      }
    }

    return {
      totalUsers: userIds.length,
      activeUsers: allUsers.length,
      inactiveUsers: inactiveCount,
      totalAssessments,
      storageSize: `${Math.round(storageSize / 1024)} KB`
    };
  }

  // 🧹 清理所有临时用户数据
  async clearAllTempUsers(): Promise<void> {
    console.log('🧹 [LocalUserService] 开始清理所有临时用户数据...');

    try {
      // 获取所有用户ID
      const userIds = this.getUserIndex();
      let cleanedCount = 0;

      // 删除所有用户数据
      for (const userId of userIds) {
        if (userId.startsWith('local_user_')) {
          await this.deleteUserRecord(userId);
          cleanedCount++;
        }
      }

      // 清理用户索引
      localStorage.removeItem(LOCAL_USER_INDEX_KEY);

      // 清理评估历史
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('assessmentHistory_')) {
          localStorage.removeItem(key);
        }
      }

      console.log(`✅ [LocalUserService] 清理完成，共删除 ${cleanedCount} 个临时用户`);
    } catch (error) {
      console.error('❌ [LocalUserService] 清理临时用户数据失败:', error);
    }
  }
}

// 导出单例实例
export const localUserService = new LocalUserService();
export default LocalUserService;